/* eslint-disable react/prop-types */
import React, { createContext, FC, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { ExternalQuestion, QuestionError } from '@sm/question-definitions';
import { ExternalValidator, OpenEndedAnswerType } from '@sm/question-widgets/respondent-survey';

import { GetCurrentSurveySessionQuery } from '~lib/generatedGqlTypes';

import {
  createPageQuestionValidator,
  getPreviousValidationErrors,
  validateAllQuestions,
  validateQuestion,
} from './validation/helpers';
import { ValidationResultTuple, QuestionsValidators } from './validation/types';

import { SurveyContext } from './SurveyContext';
import { QuestionAnswers } from './types';

type CurrentPageContextType = {
  questions?: NonNullable<
    NonNullable<GetCurrentSurveySessionQuery['surveyTakingSession']['respondent']['currentPage']>['questionsQM']
  >['items'];
  answers?: QuestionAnswers;
  totalAnswers: number;
  updateAnswer(question: ExternalQuestion, answer: OpenEndedAnswerType): void;
  registerValidator(questionId: string, newValidators: ExternalValidator[]): void;
  unregisterValidator(questionId: string): void;
  validatePage(): ValidationResultTuple;
  questionAnswerErrors(questionId?: string): QuestionError[];
};

export const CurrentPageContext = createContext<CurrentPageContextType>({
  questions: undefined,
  answers: undefined,
  totalAnswers: 0,
  updateAnswer: () => {},
  registerValidator: () => {},
  unregisterValidator: () => {},
  validatePage: () => [false, []],
  questionAnswerErrors: () => [],
});

type CurrentPageProviderProps = {
  children: React.ReactNode;
};

export const CurrentPageProvider: FC<CurrentPageProviderProps> = ({ children }) => {
  // Local storage -> overrides data from survey.
  // Local storage should be cleared when we hit the next page button.

  const { activePage } = useContext(SurveyContext);

  const [questions] = useState(activePage?.questionsQM?.items);
  const [, setPageValidationStatus] = useState(true);

  const pageQuestionValidators = useRef<QuestionsValidators>({});

  const initialAnswers = questions?.reduce(
    (acc, curr) => ({ ...acc, [curr.id]: { questionId: curr.id, value: '', touched: false, isDirty: false } }),
    {}
  );

  // Hydrate answers
  const [answers, setAnswers] = useState<QuestionAnswers>(initialAnswers || {});

  // context functions
  const updateAnswer = useCallback<CurrentPageContextType['updateAnswer']>(
    (question, answer) => {
      if (!answer.touched) {
        return; // no need for updates if answer was not touched
      }
      const { current: currentPageValidators } = pageQuestionValidators;
      const { questionId, value: answerValue } = answer;

      if (currentPageValidators[questionId]) {
        // validate answer
        currentPageValidators[questionId].lastResult = validateQuestion(
          currentPageValidators[questionId],
          questionId,
          answerValue
        );
      }

      // keep answer, regardless of validity
      answers[questionId] = answer; // need to update the answers object in order for totalAmount to work properly

      setAnswers({ ...answers, [questionId]: answer }); // rebuild new state object to store answers
    },
    [answers]
  );

  const unregisterValidator = (questionId: string): void => {
    const { current: currentPageValidators } = pageQuestionValidators;

    currentPageValidators[questionId] = createPageQuestionValidator(
      null,
      currentPageValidators[questionId]?.lastResult
    );
  };

  const registerValidator = (questionId: string, newValidators: ExternalValidator[]): void => {
    const { current: currentPageValidators } = pageQuestionValidators;

    currentPageValidators[questionId] = createPageQuestionValidator(
      newValidators,
      currentPageValidators[questionId]?.lastResult
    );
  };

  /**
   * the total number of questions that have a value (i.e. answered), on the current page only
   * TODO: refactor to include answered questions on other pages once we get that data from the backend
   *  */
  const totalAnswers = useMemo(
    () => (!answers ? 0 : Object.values(answers).reduce((acc: number, answer) => (answer.value ? acc + 1 : acc), 0)),
    [answers]
  );

  const validatePage = useCallback((): ValidationResultTuple => {
    const validationResult = validateAllQuestions(pageQuestionValidators.current, answers);
    const [isValid] = validationResult;

    setPageValidationStatus(isValid);

    return validationResult;
  }, [answers]);

  const questionAnswerErrors = (questionId?: string): QuestionError[] => {
    const [, messages] = getPreviousValidationErrors(pageQuestionValidators.current, questionId) ?? [];

    return (messages || []).map<QuestionError>(validationMessage => ({
      // NOTE: currently this implementation is incorrect, however, there is no present way
      // to apply the correct type for QuestionError.
      // Will be fixed in [RAWR-935](https://jira.surveymonkey.com/browse/RAWR-935)
      type: 'REQUIRED',
      detail: validationMessage.detail,
      questionId: validationMessage.questionId,
    }));
  };

  return (
    <CurrentPageContext.Provider
      value={{
        questions,
        answers,
        totalAnswers,
        updateAnswer,
        registerValidator,
        unregisterValidator,
        validatePage,
        questionAnswerErrors,
      }}
    >
      <>{children}</>
    </CurrentPageContext.Provider>
  );
};

export default CurrentPageProvider;
